import { KeyValueObj } from "@/common/types";
import { ReactNode } from "react";

interface IInventoryDetails {
  data: KeyValueObj;
}

const InfoItem = ({
  label,
  value,
}: {
  label: string;
  value: string | number | ReactNode;
}) => (
  <div className="flex w-full gap-2 my-2">
    <span className="text font-bold text-gray-600">{label}:</span>
    <span className="text-gray-800">{value}</span>
  </div>
);

// MED-646: derive one label per packaging level from the product's relational
// packaging_layers (ordered outermost -> base). level N maps to index N-1.
// Falls back to a generic "Level N" when the hierarchy isn't available.
function levelLabels(product?: KeyValueObj): Record<number, string> {
  const layers: KeyValueObj[] = Array.isArray(product?.packaging_layers)
    ? product!.packaging_layers
    : [];
  const labels: Record<number, string> = {};
  layers.forEach((layer, idx) => {
    labels[idx + 1] = layer?.packaging_type || `Level ${idx + 1}`;
  });
  return labels;
}

// Group native serials by their level (1..N). Works off the N-level
// `serial_units` table (level + serial_number), replacing the fixed
// carton_units/pack_units/unit_items rendering.
function serialsByLevel(
  serialUnits: KeyValueObj[],
): Array<{ level: number; serials: string[] }> {
  const byLevel: Record<number, string[]> = {};
  (serialUnits || []).forEach((unit) => {
    const level = Number(unit?.level);
    if (!level) return;
    (byLevel[level] = byLevel[level] || []).push(unit?.serial_number);
  });
  return Object.keys(byLevel)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .map((level) => ({ level, serials: byLevel[level] }));
}

function InventoryDetails({ data }: IInventoryDetails) {
  const labels = levelLabels(data?.product);
  const serialUnits: KeyValueObj[] = Array.isArray(data?.serial_units)
    ? data.serial_units
    : [];
  const grouped = serialsByLevel(serialUnits);

  return (
    <div className=" w-full">
      <div className=" bg-white shadow-md rounded-lg ">
        {/* Header */}
        <div className="p-5 space-y-6">
          {/* Product Info */}
          <div className="grid md:grid-cols-2 gap-4  text-sm">
            <InfoItem label="Product Name" value={data?.product?.brand_name} />
            <InfoItem label="Batch ID" value={data?.batch_number} />
            <InfoItem label="Manufacture Date" value={data?.manufacture_date} />
            <InfoItem label="Expiry Date" value={data?.expiry_date} />
            <InfoItem
              label="Selling Price (₦)"
              value={data?.selling_price_per_unit}
            />
            <InfoItem label="Stock Quantity" value={data?.quantity_in_stock} />
          </div>

          {/* Serials — one column per packaging level, driven by the product
              hierarchy instead of hardcoded Carton/Pack/Unit. */}
          {grouped.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-3 mt-10">
                Serials
              </h2>
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: `repeat(${grouped.length}, minmax(0, 1fr))`,
                }}
              >
                {grouped.map(({ level, serials }) => (
                  <div key={level} className="border rounded-md">
                    <div className="bg-gray-100 px-3 py-2 font-medium text-gray-700 text-sm sticky top-0">
                      {labels[level] || `Level ${level}`} Serial ({serials.length})
                    </div>
                    <div className="max-h-[300px] overflow-auto divide-y">
                      {serials.map((serial, index) => (
                        <div
                          key={`${level}-${index}`}
                          className="text-sm py-2 px-3"
                        >
                          {serial}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InventoryDetails;
